import { css } from '@emotion/core'
import React, {
  createContext,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { hasValidChildren } from '../hasValidChildren'
import useProcessedHeights from './useProcessedHeights'

const centerCss = css`
  display: flex;
  align-items: center;
  justify-content: center;
`

type CompareType = `compare${number}`
type HeightById = Record<Exclude<CompareType, 'resultant'>, number>
type Heights = Partial<Record<CompareType, HeightById>>
type ObserverRefProps = {
  ref: RefObject<HTMLDivElement>
  skipDelay?: boolean
}
type HeightContextValue = {
  maxHeights: HeightById
  observeRef: ({ ref }: ObserverRefProps) => void
  unobserveRef: ({ ref }: ObserverRefProps) => void
}

const HeightContext = createContext<HeightContextValue | null>(null)

type HeightDivProps = {
  type: CompareType
  id: string
  children?: ReactNode
  centerContents?: boolean
  additionalHeight?: number
  /** When to skip the 50ms debounce delay for this HeightDiv. Use when you have expanders or animations that change height continuously. */
  skipDelay?: boolean
  'data-testid'?: string
}

const useHeightContext = (): HeightContextValue => {
  const context = useContext(HeightContext)
  if (!context) throw new Error('useHeightContext needs a HeightProvider as a parent.')
  return context
}

/** To get compare types heights and use their max height for the types per id. */
export const HeightDiv = ({
  type,
  id,
  children,
  centerContents,
  additionalHeight = 0,
  skipDelay = false,
  'data-testid': dataTestId,
}: HeightDivProps): JSX.Element | null => {
  const ref = useRef<HTMLDivElement>(null)
  const { maxHeights, observeRef, unobserveRef } = useHeightContext()

  useEffect(() => {
    if (ref) observeRef({ ref, skipDelay })
    return () => {
      if (ref) unobserveRef({ ref, skipDelay })
    }
  }, [type, ref, observeRef, unobserveRef, skipDelay])

  const maxHeight: number | undefined = maxHeights[id]

  if (!hasValidChildren(children) && maxHeight === 0) return null

  return (
    <div
      style={maxHeight ? { minHeight: maxHeight } : {}}
      css={centerContents ? centerCss : undefined}
      data-testid={dataTestId}
    >
      <div ref={ref} data-type={type} data-id={id}>
        {children}
        <div style={{ height: additionalHeight }} />
      </div>
    </div>
  )
}

/** To sync up heights for the same HeightDiv types. */
export default function HeightProvider({ children }: { children: ReactNode }): JSX.Element {
  const [compareHeights, setCompareHeights] = useState<Heights>({})
  const skipDelayIdsRef = useRef<Set<string>>(new Set())

  const processedHeights = useProcessedHeights(compareHeights, {
    debounceMs: 50,
    skipDelayIdsRef,
  })

  const maxHeights = useMemo(
    () =>
      Object.values(processedHeights).reduce<HeightById>((accum, value) => {
        if (value) {
          Object.entries(value).forEach(([id, height]) => {
            const currentHeight = accum[id]
            if ((currentHeight === undefined || currentHeight < height) && height !== undefined) accum[id] = height
          })
        }
        return accum
      }, {}),

    [processedHeights]
  )

  const resizeObserver = useMemo(
    () =>
      new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const { type, id } = (entry.target as HTMLElement).dataset
          const height = entry.contentRect.height

          if (type && id) {
            setCompareHeights((prev) => {
              const newState = { ...prev }
              newState[type] ??= {}
              newState[type][id] = height
              return newState
            })
          }
        })
      }),
    []
  )

  useEffect(() => {
    return () => {
      resizeObserver.disconnect()
    }
  }, [resizeObserver])

  const observeRef = useCallback(
    ({ ref, skipDelay }: ObserverRefProps): void => {
      if (ref.current) {
        resizeObserver.observe(ref.current)
        const { id } = ref.current.dataset
        if (skipDelay && id) {
          skipDelayIdsRef.current.add(id)
        }
      }
    },
    [resizeObserver]
  )

  const unobserveRef = useCallback(
    ({ ref, skipDelay }: ObserverRefProps): void => {
      if (ref.current) {
        resizeObserver.unobserve(ref.current)
        const { id } = ref.current.dataset
        if (skipDelay && id) {
          skipDelayIdsRef.current.delete(id)
        }
      }
    },
    [resizeObserver]
  )

  const value = useMemo(
    () => ({
      observeRef,
      unobserveRef,
      maxHeights,
    }),
    [maxHeights, observeRef, unobserveRef]
  )

  return <HeightContext.Provider value={value}>{children}</HeightContext.Provider>
}
